import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, Ship, Receipt, Trophy, MessageCircle, Lock } from "lucide-react";
import { MyOrders, SafeBox } from "./MyOrders";
import { useLang } from "../../lib/i18n";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, eur, fmtDate, fileUrl } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { FileUpload, StatusBadge } from "../../components/site/FileUpload";
import { H } from "./AccountPages";

const ReceiptForm = ({ r, onDone }) => {
  const { t } = useLang();
  const [f, setF] = useState({ account: r.accounts.find((a) => a.balance > 0)?.account || "clinic", amount: "", path: "", filename: "" });
  const submit = async () => {
    if (!f.path || !f.amount) return toast.error(`${t("amount")} + ${t("upload_receipt")}`);
    try {
      await api.post("/me/receipts", { ...f, registration_id: r.id, amount: Number(f.amount) });
      toast.success(t("saved"));
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-4 flex flex-wrap items-end gap-3 rounded-2xl bg-slate-50 p-4" data-testid={`receipt-form-${r.id}`}>
      <select value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} className="h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm" data-testid="receipt-account-select">
        {r.accounts.map((a) => <option key={a.account} value={a.account}>{a.account}</option>)}
      </select>
      <Input type="number" placeholder={t("amount")} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="h-10 w-32 rounded-xl" data-testid="receipt-amount-input" />
      <FileUpload label={t("upload_receipt")} value={f.filename} onUploaded={(x) => setF({ ...f, path: x.path, filename: x.filename })} testid="receipt-file-upload" />
      <button onClick={submit} className="btn-buoy !py-2" data-testid="receipt-submit">{t("send")}</button>
    </div>
  );
};

const RegCard = ({ r, reload }) => {
  const { t, tr, ts, lang } = useLang();
  const [showForm, setShowForm] = useState(false);
  const accLabel = { clinic: t("clinic_fee"), charter: t("charter"), services: t("services_acc") };
  const cancel = async () => {
    if (!window.confirm(t("cancel_reg") + "?")) return;
    await api.post(`/me/registrations/${r.id}/cancel`);
    reload();
  };
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid={`registration-card-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 p-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#00A8B5]" data-testid={`reg-participant-${r.id}`}>{r.participant?.first_name} {r.participant?.last_name}</p>
          <p className="font-display text-xl font-bold text-[#0A2540]">{tr(r.event, "title")}</p>
          <p className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
            <span className="flex items-center gap-1" data-testid={`reg-dates-${r.id}`}><CalendarDays size={14} />{fmtDate(r.participation_start || r.event.start_date, lang)} – {fmtDate(r.participation_end || r.event.end_date, lang)}</span>
            {r.class_name && <span className="font-semibold text-[#00A8B5]" data-testid={`reg-class-${r.id}`}>{r.class_name}</span>}
            {r.event.race?.name && <span className="flex items-center gap-1" data-testid={`reg-race-${r.id}`}><Trophy size={14} />{t("race")}: {r.join_race ? "✓" : "—"}</span>}
            <span className="flex items-center gap-1"><Ship size={14} />{r.boat_option === "charter" ? `${t("charter_boat")}: ${r.charter?.model || ""}` : `${t("own_boat")} ${r.own_boat?.sail_number || ""}`}</span>
          </p>
          {r.event.whatsapp_link
            ? <a href={r.event.whatsapp_link} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-[#25D366] px-4 py-1.5 text-sm font-semibold text-[#128C7E] transition-colors hover:bg-[#25D366]/10" data-testid={`reg-whatsapp-link-${r.id}`}><MessageCircle size={15} />{t("whatsapp_event")}</a>
            : r.whatsapp_locked && r.status !== "cancelled" && <p className="mt-3 flex items-center gap-2 text-xs text-slate-400" data-testid={`reg-whatsapp-locked-${r.id}`}><Lock size={13} />{t("whatsapp_locked")}</p>}
        </div>
        <div className="flex gap-2"><StatusBadge status={r.status} label={ts(r.status)} testid={`reg-status-${r.id}`} /><StatusBadge status={r.payment_status} label={ts(r.payment_status)} testid={`reg-payment-status-${r.id}`} /></div>
      </div>
      <div className={`grid gap-px bg-slate-100 ${["sm:grid-cols-1", "sm:grid-cols-1", "sm:grid-cols-2", "sm:grid-cols-3"][r.accounts.length] || "sm:grid-cols-3"}`}>
        {r.accounts.map((a) => (
          <div key={a.account} className="bg-white p-5" data-testid={`reg-account-${r.id}-${a.account}`}>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{accLabel[a.account] || a.account}</p>
            <p className="mt-1 font-display text-2xl font-extrabold text-[#0A2540]">{eur(a.total)}</p>
            <p className="text-xs text-slate-500">{t("paid")} {eur(a.paid - a.refunded)} · {t("balance")} {eur(a.balance)}</p>
            <div className="mt-2"><StatusBadge status={a.status} label={ts(a.status)} /></div>
          </div>
        ))}
      </div>
      <div className="p-6">
        {(r.payments.length > 0 || r.receipts.length > 0) && (
          <div className="mb-4 grid gap-4 md:grid-cols-2 text-sm">
            <div><p className="mb-2 font-semibold text-[#0A2540]">{t("payments_history")}</p>{r.payments.map((p) => <p key={p.id} className="flex justify-between text-slate-600"><span>{fmtDate(p.date, lang)} · {p.account} · {p.kind}</span><span className={p.kind === "refund" ? "text-rose-600" : ""}>{p.kind === "refund" ? "-" : ""}{eur(p.amount)}</span></p>)}</div>
            <div><p className="mb-2 font-semibold text-[#0A2540]">{t("receipts")}</p>{r.receipts.map((x) => <p key={x.id} className="flex items-center justify-between gap-2 text-slate-600"><a href={fileUrl(x.path)} target="_blank" rel="noreferrer" className="flex items-center gap-1 underline"><Receipt size={13} />{eur(x.amount)} · {x.account}</a><StatusBadge status={x.status} label={ts(x.status)} /></p>)}</div>
          </div>
        )}
        <div className="flex flex-wrap gap-3">
          {r.status !== "cancelled" && r.balance > 0 && !r.order_id && <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center gap-2 rounded-full bg-[#0A2540] px-4 py-2 text-sm font-semibold text-white" data-testid={`reg-upload-receipt-${r.id}`}><Receipt size={15} />{t("upload_receipt")}</button>}
          {r.status !== "cancelled" && r.event.status !== "Completed" && r.accounts.length > 0 && <button onClick={cancel} className="text-sm font-semibold text-slate-400 hover:text-rose-500" data-testid={`reg-cancel-${r.id}`}>{t("cancel_reg")}</button>}
        </div>
        {showForm && <ReceiptForm r={r} onDone={() => { setShowForm(false); reload(); }} />}
      </div>
    </div>
  );
};

export default function MyRegistrations() {
  const { t } = useLang();
  const { data, reload } = useFetch("/me/registrations");
  return (
    <div data-testid="account-registrations">
      <H>{t("acc_regs")}</H>
      <MyOrders />
      {data === null && <p className="rounded-2xl bg-rose-50 p-4 text-sm text-rose-700" data-testid="registrations-load-error">{t("load_error")} <button onClick={reload} className="font-semibold underline">{t("retry")}</button></p>}
      {data && !data.length && <p className="text-slate-500">{t("no_items")}</p>}
      <div className="space-y-6">{(data || []).map((r) => <SafeBox key={r.id} fallback={<CardFallback r={r} />}><RegCard r={{ ...r, event: r.event || {}, accounts: r.accounts || [], payments: r.payments || [], receipts: r.receipts || [] }} reload={reload} /></SafeBox>)}</div>
    </div>
  );
}

const CardFallback = ({ r }) => {
  const { tr, ts } = useLang();
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid={`registration-card-${r.id}`}>
      <p className="font-display text-lg font-bold text-[#0A2540]">{tr(r.event || {}, "title")}</p>
      <p className="text-sm text-slate-500">{r.participant?.first_name} {r.participant?.last_name}</p>
      <div className="mt-2 flex gap-2"><StatusBadge status={r.status} label={ts(r.status)} testid={`reg-status-${r.id}`} />{r.payment_status && <StatusBadge status={r.payment_status} label={ts(r.payment_status)} testid={`reg-payment-status-${r.id}`} />}</div>
    </div>
  );
};

